import { create } from 'zustand';

/**
 * The locale, timezone and currency every formatter resolves against.
 *
 * Resolution order, once the later phases fill it in: the person's own preference,
 * then the church's default, then what the browser reports, then the platform
 * fallback. Phase 0 has no session, so it starts at the browser.
 *
 * Kept in one place because a formatter that reads the browser directly is a
 * formatter that shows a Ghanaian church's service time in a visiting
 * administrator's timezone.
 */

export interface Preferences {
  locale: string;
  timezone: string;
  /** The church's reporting currency. Money inputs default to it. */
  baseCurrency: string;
}

interface PreferencesState extends Preferences {
  /** Applied when a session resolves; unset values keep their current resolution. */
  apply: (next: Partial<Preferences>) => void;
}

const browserLocale = (): string => {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language || 'en';
};

const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Accra';
  } catch {
    return 'Africa/Accra';
  }
};

export const usePreferences = create<PreferencesState>((set) => ({
  locale: browserLocale(),
  timezone: browserTimezone(),
  baseCurrency: 'GHS',
  apply: (next) =>
    set((current) => ({
      locale: next.locale ?? current.locale,
      timezone: next.timezone ?? current.timezone,
      baseCurrency: next.baseCurrency ?? current.baseCurrency,
    })),
}));

/** For formatters called outside a component, such as a table column renderer. */
export const currentPreferences = (): Preferences => {
  const { locale, timezone, baseCurrency } = usePreferences.getState();
  return { locale, timezone, baseCurrency };
};
